/** @jsx jsx */
import React, { useEffect } from 'react';
import { css, jsx } from '@emotion/react'
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image"
import { graphql } from 'gatsby';
import { useLocalization } from "gatsby-theme-i18n";
import PageHeadline from '../components/PageHeadline';
import { H2, P, HeadlineWrapper, HeadlineIcon, Anchor } from "../components/StyledComponents";
import Logo from "../components/StyledComponents/Logo";
import EventCard from "../components/StyledComponents/EventCard";
import RoundButton from '../components/RoundButton';
import Seo from "../components/SEO"
import * as breakpoint from '../styles/breakpoints.module.scss';
import styled from '@emotion/styled';
import AnimatedSection from '../components/AnimatedSection';
import AlertBox from '../components/AlertBox'
import PageNavigation from '../components/PageNavigation'
import { DateTime } from 'luxon';
import IconPackage from 'xiv-fanfes-2021'

import CrosssiteBannerSection from '../sections/common/CrosssiteBannerSection';
import CommonSection,{
  SectionTextColumn,
  SectionImageColumn,
  SectionFullWidthColumn,
  SectionRow
} from "../components/CommonSection";

import { parseHTML } from '../functions/common'

const AboutPage = ({data}) => {
  const { t, addString, currentLocaleObj, localeKey } = useLocalization();

  useEffect(() => {
    addString('about',data.allAboutCsv.nodes)
  },[addString,data]);


  return(
    <React.Fragment>
      <Seo 
        title={data.pageTitle[localeKey]} 
        description={data.pageDescription[localeKey]}  />  
      <PageHeadline category={t('about.page.category.label')} title={t('about.page.display.title')} bgImg={
        withArtDirection(getImage(data.headlineBg), [
          {
            media: `(min-width: ${breakpoint.mdMin})`,
            image: getImage(data.headlineBg),
          },
          {
            media: `(max-width: ${breakpoint.smMax}) and (min-width: ${breakpoint.smMin})`,
            image: getImage(data.headlineBg_tablet),
          },
          {
            media: `(max-width: ${breakpoint.xsMax})`,
            image: getImage(data.headlineBg_mobile),
          }
        ])}/>
      <PageNavigation list={[
        {
          id: 'schedule',
          name: t('about.date.title'),
          isNew:  DateTime.fromISO('2021-04-22T17:00:00+09:00').diff(DateTime.local(), [ 'days']).values.days > -3
        },
        {
          id: 'streaming',
          name: t('about.streaming.title'),
          isNew:  DateTime.fromISO('2021-04-22T17:00:00+09:00').diff(DateTime.local(), [ 'days']).values.days > -3
        },
        {
          id: 'location',
          name: t('about.venue.title'),
          isNew:  DateTime.fromISO('2021-03-29T17:00:00+09:00').diff(DateTime.local(), [ 'days']).values.days > -3
        },
        {
          id: 'ticket',
          name: t('about.ticket.title'),
          isNew:  DateTime.fromISO('2021-04-27T17:00:00+09:00').diff(DateTime.local(), [ 'days']).values.days > -3
        }
      ]}/>
      <AnimatedSection>
        <CommonSection>
          <SectionFullWidthColumn css={css`text-align:center;`}>
            <Logo css={css`
              margin-bottom:50px;
              @media only screen and (max-width: ${breakpoint.smMax}){
                margin-bottom:48px;
              }
              @media only screen and (max-width: ${breakpoint.xsMax}){
                margin-bottom:16px;
              }
            `}/>
            <HeadlineWrapper>
              <H2>{t('about.introduction.title')}</H2>
            </HeadlineWrapper>
            <P align={'left'}>{parseHTML(t('about.introduction.lead'),true)}</P>
          </SectionFullWidthColumn>
        </CommonSection>
      </AnimatedSection>
      <Anchor id={'schedule'}>{t('about.date.title')}</Anchor>
      <AnimatedSection >
        <CommonSection>
          <SectionFullWidthColumn>
            <HeadlineWrapper>
              <H2>{t('about.date.title')}</H2>
            </HeadlineWrapper>
          </SectionFullWidthColumn>
          <SectionRow>
            <EventCard>
              <div className="top">
                <div>{t('event.date.day1.label')}</div>
                {parseHTML(t('about.date.day1.date'))}
              </div>
              <div className="time">
                <ArrowButton color={'#4E4E50'} size={'small'} witharrow={"true"} isAnchor={true} to={`/${currentLocaleObj.prefix}/program#day1`} target="_blank" css={css`
                `}>{t('about.date.day1.program.button')}</ArrowButton>
                <ArrowButton color={'#4E4E50'} size={'small'} witharrow={"true"} isAnchor={true} to={`/${currentLocaleObj.prefix}/program/timetable#day1`} target="_blank" css={css`
                `}>{t('about.date.day1.timetable.button')}</ArrowButton>
              </div>
            </EventCard>
            <EventCard>
              <div className="top">
                <div>{t('event.date.day2.label')}</div>
                {parseHTML(t('about.date.day2.date'))}
              </div>
              <div className="time">
                <ArrowButton color={'#4E4E50'} size={'small'} witharrow={"true"} isAnchor={true} to={`/${currentLocaleObj.prefix}/program#day2`} target="_blank" css={css`
                `}>{t('about.date.day2.program.button')}</ArrowButton>
                <ArrowButton color={'#4E4E50'} size={'small'} witharrow={"true"} isAnchor={true} to={`/${currentLocaleObj.prefix}/program/timetable#day2`} target="_blank" css={css`
                `}>{t('about.date.day2.timetable.button')}</ArrowButton>
              </div>
            </EventCard>
          </SectionRow>
          <SectionRow css={css`
            margin-top:48px;
            @media only screen and (max-width: ${breakpoint.smMax}){
              margin-top:32px;
            }
            @media only screen and (max-width: ${breakpoint.xsMax}){
              margin-top:16px;
            }
          `}>
            <AlertBox iconType={'Alert'} iconColor={'#D00F31'} text={ t('about.date.attention')} type={'narrow'} css={css`
              width:304px;

              @media only screen and (max-width: ${breakpoint.xsMax}){
                width:100%;
              }
            `}/>
          </SectionRow>
        </CommonSection>
      </AnimatedSection>
      <Anchor id={'streaming'}>{t('about.streaming.title')}</Anchor>
      <AnimatedSection>
        <CommonSection>
          <SectionFullWidthColumn>
            <HeadlineWrapper>
              <H2>{t('about.streaming.title')}</H2>
            </HeadlineWrapper>
            <P>{parseHTML(t('about.streaming.description'))}</P>
            <StremingButtonContainer>
              <StreamingButton color={'#4E4E50'} size={'medium'} href={t('nav.streaming.twitch.url')} disabled={t('nav.streaming.twitch.url') === ""} target="_blank">
                <IconPackage.Icon type={'Twitch'} alt={t('nav.streaming.twitch.label')} />
                <span className="text">{t('nav.streaming.twitch.label')}</span>
              </StreamingButton>
              <div className="noBreak">
                <StreamingButton color={'#4E4E50'} size={'medium'} href={t('nav.streaming.youtube.day1.url')} disabled={true} target="_blank">
                  <IconPackage.Icon type={'Youtube'} alt={`${t('nav.streaming.youtube.label')} ${t('event.date.day1.label')}`} />
                  <span className="text">{`${t('nav.streaming.youtube.label')} ${t('event.date.day1.label')}`}</span>
                </StreamingButton>
                <StreamingButton color={'#4E4E50'} size={'medium'} href={t('nav.streaming.youtube.day2.url')} disabled={true} target="_blank">
                  <IconPackage.Icon type={'Youtube'} alt={`${t('nav.streaming.youtube.label')} ${t('event.date.day2.label')}`} />
                  <span className="text">{`${t('nav.streaming.youtube.label')} ${t('event.date.day2.label')}`}</span>
                </StreamingButton>
              </div>
              <div className="noBreak">
                <StreamingButton color={'#4E4E50'} size={'medium'} href={t('nav.streaming.niconico.day1.url')} disabled={t('nav.streaming.niconico.day1.url') === ""} target="_blank">
                  <IconPackage.Icon type={'Niconico'} alt={`${t('nav.streaming.niconico.label')} ${t('event.date.day1.label')}`} />
                  <span className="text">{`${t('nav.streaming.niconico.label')} ${t('event.date.day1.label')}`}</span>
                </StreamingButton>
                <StreamingButton color={'#4E4E50'} size={'medium'} href={t('nav.streaming.niconico.day2.url')} disabled={t('nav.streaming.niconico.day2.url') === ""} target="_blank">
                  <IconPackage.Icon type={'Niconico'} alt={`${t('nav.streaming.niconico.label')} ${t('event.date.day2.label')}`} />
                  <span className="text">{`${t('nav.streaming.niconico.label')} ${t('event.date.day2.label')}`}</span>
                </StreamingButton>
              </div>
            </StremingButtonContainer>
          </SectionFullWidthColumn>
        </CommonSection>
      </AnimatedSection>
      <Anchor id={'location'}>{t('about.venue.title')}</Anchor>
      <AnimatedSection>
        <CommonSection>
          <SectionTextColumn>
            <HeadlineWrapper>
              <HeadlineIcon type={'MapMarker'} color={'#D00F31'} css={css`margin-left:0;`}/>
              <H2>{t('about.venue.title')}</H2>
            </HeadlineWrapper>
            <P css={css`margin-bottom:24px;`}>{t('event.venue.name')}<br/>{t('event.venue.address')}</P>
            {t('event.venue.url') !== "" &&
              <RoundButton color={'#4E4E50'} size={'small'} href={t('event.venue.url')} target="_blank" css={css`margin-right:16px;
              @media only screen and (max-width: ${breakpoint.xsMax}){
                width:122px;
                padding:0;
              }
              `}>{t('utils.button.details.label')}</RoundButton>
            }
            {t('about.venue.googlemap.button.label') !== "" &&
              <RoundButton color={'#4E4E50'} size={'small'} href={t('event.venue.googlemap.url')} target="_blank" css={css`
              @media only screen and (max-width: ${breakpoint.xsMax}){
                width:122px;
                padding:0;
              }`}>{t('about.venue.googlemap.button.label')}</RoundButton>
            }
          </SectionTextColumn>
          <SectionImageColumn>
            <GatsbyImage image={getImage(data.venue_img)} alt={t('event.venue.name')}/>
          </SectionImageColumn>
        </CommonSection>
      </AnimatedSection>
      {currentLocaleObj.code === "ja" &&
      <React.Fragment>
      <Anchor id={'ticket'}>{t('about.ticket.title')}</Anchor>
      <AnimatedSection>
        <div css={css`
          padding:0 48px;

          @media only screen and (max-width: ${breakpoint.smMax}){
            padding:0;
          }
        `}>
          <CommonSection css={css`
            max-width:1440px;
            background: #F2F2F5;
            border: 1px solid #CACACD;
            box-sizing: border-box;
            border-radius: 6px;
            margin:0 auto;

            @media only screen and (max-width: ${breakpoint.smMax}){
              border-radius: 0px;
              border-left: 0px;
              border-right: 0px;
            }
          `}>
            
            <SectionRow>
              <TicketNotice>
                <h2 className="title">{t('about.ticket.title')}</h2>
                <div className="description">
                  <p>{t('about.ticket.description.line1')}</p>
                  <p>{t('about.ticket.description.line2')}</p>
                  <p>{t('about.ticket.description.line3')}</p>
                  <p>{t('about.ticket.description.line4')}</p>
                </div>
              </TicketNotice>
            </SectionRow>
          </CommonSection>
        </div>
      </AnimatedSection>
      </React.Fragment>
      }
      <AnimatedSection>
        <CrosssiteBannerSection />
      </AnimatedSection>
      
    </React.Fragment>
  );
};

export default AboutPage;


export const query = graphql`
  query AboutPageQuery {
    pageTitle : aboutCsv(key: {regex: "/^page.title/"}) {
      ...localizedContent
    }
    pageDescription :aboutCsv(key: {regex: "/^page.description/"}) {
      ...localizedContent
    }
    pageKeywords : aboutCsv(key: {regex: "/^page.keywords/"}) {
      ...localizedContent
    }

    allAboutCsv {
      nodes {
        ...localizedContent
      }
    }
    venue_img : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "content/about_venue.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }
    headlineBg : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/bg_pageHeader_about.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }
    headlineBg_tablet : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/bg_pageHeader_about_tablet.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }
    headlineBg_mobile : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/bg_pageHeader_about_mobile.jpg"}) {
      childImageSharp {
        gatsbyImageData(
          layout: FULL_WIDTH
          placeholder: NONE
        )
      }
    }
  }
`

const StremingButtonContainer = styled.div`
  margin-top:48px;

  .noBreak{
    display:inline-block;
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    margin-top:32px;
    
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    margin-top:24px;
    text-align:center;
    .noBreak{
      display:block;
    }
  }
`

const StreamingButton = styled(RoundButton)`
  margin-right:32px;
  padding:10px 22px;
  height:auto;
  margin-bottom:16px;

  svg{
    width:24px;
    height:24px;
    margin-right:12px;
    vertical-align:middle;
  }
  span.text{
    font-size: 16px;
    line-height: 24px;
    vertical-align:middle;
  }



  @media only screen and (max-width: ${breakpoint.smMax}){
    
    padding:10px 22px;
    height:auto;
    border-radius:48px;
    margin-right:16px;
    
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    display:block;
    padding:5px 22px;
    width:100%;
    margin-right:0;
    margin-bottom:8px;

    svg{
      width:20px;
      height:20px;
      margin-right:8px;
      vertical-align:middle;
    }
    span.text{
      font-size: 14px;
      line-height: 20px;
      vertical-align:middle;
    }
  }
`

const ArrowButton = styled(RoundButton)`
  width:100%;
  display:block;
  margin-bottom:16px;
  padding:10px 24px;
  border-radius: 24px;
  height:auto;
  > span{
    justify-content: flex-start;
    font-size: 16px;
    line-height: 24px;
  }
  &:last-of-type{
    margin-bottom:0 !important;
  }
  &:after{
    right:32px;
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    padding:6px 16px;
    margin-bottom:8px;
    > span{
      font-size: 14px;
      line-height: 20px;
    }
    &:after{
      width:6px;
      height:6px;
      right:23px;
      margin-top: -3px;
    }
  }

`

const TicketNotice = styled.div`

  .title {
    margin-bottom: 3rem;
    font-weight: 900;
    font-size: 48px;
    line-height: 56px;
    letter-spacing: 0.01em;
    color: #2C2C2E;
    
    @media only screen and (max-width: ${breakpoint.smMax}){
      margin-bottom: 2rem;
      font-size: 36px;
      line-height: 48px;
    }

    @media only screen and (max-width: ${breakpoint.xsMax}){
      margin-bottom: 1rem;
      font-size: 18px;
      line-height: 125%;
    }
  }

  .button {
    margin:48px 0;
    width:288px;

    @media only screen and (max-width: ${breakpoint.smMax}){
      margin:32px 0;
      height:48px;
      font-size: 16px;
      line-height: 24px;
    }

    @media only screen and (max-width: ${breakpoint.xsMax}){
      margin:24px 0;
      height:36px;
      width:160px;
      font-size: 14px;
      line-height: 20px;
    }
  }

  .description {
    margin-bottom: 3rem;

    &:last-child {
      margin-bottom: 0;
    }

    @media only screen and (max-width: ${breakpoint.smMax}){
      margin-bottom: 2rem;
    }

    @media only screen and (max-width: ${breakpoint.xsMax}){
      margin-bottom: 1rem;
    }

    p {
      margin-bottom: 1rem;
      font-size: 16px;
      line-height: 28px;
      color: #444448;

      @media only screen and (max-width: ${breakpoint.smMax}){
        font-size: 14px;
        line-height: 24px;
      }

      @media only screen and (max-width: ${breakpoint.xsMax}){
        font-size: 12px;
        line-height: 20px;
      }
    }
  }

  .annotation {
    font-size: 14px;
    line-height: 24px;
    color: #787880;

    @media only screen and (max-width: ${breakpoint.xsMax}){
      font-size: 12px;
      line-height: 20px;
    }
  }
`
